import { useState, useEffect, useCallback } from 'react';
import { 
  Package, AlertTriangle, Plus, Minus, History, RefreshCcw, 
  TrendingDown, TrendingUp, ShoppingCart, User, Calendar, Filter,
  ClipboardCheck, Truck, BarChart3, CheckCircle, XCircle, Clock, Brain, Loader2, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';

import { API_URL } from '@/config/api';

// Change type configuration
const changeTypeConfig = {
  add: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-100', label: 'Added' },
  deduct: { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-100', label: 'Deducted' },
  manual: { icon: User, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Manual' },
  order: { icon: ShoppingCart, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Order' },
  adjustment: { icon: History, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Adjustment' }
};

export default function AdminStock() {
  const { token } = useAuth();
  const [stockItems, setStockItems] = useState([]);
  const [stockLogs, setStockLogs] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [reorderData, setReorderData] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [newQuantity, setNewQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterItem, setFilterItem] = useState('all');
  const [categories, setCategories] = useState([]);
  
  // Reconciliation state
  const [reconciliationItems, setReconciliationItems] = useState([]);
  const [reconciliationNotes, setReconciliationNotes] = useState('');
  const [submittingRecon, setSubmittingRecon] = useState(false);
  
  // Restock state
  const [restockDialogOpen, setRestockDialogOpen] = useState(false);
  const [restockItems, setRestockItems] = useState([]);
  const [restockSupplier, setRestockSupplier] = useState('');
  const [restockNote, setRestockNote] = useState('');
  const [submittingRestock, setSubmittingRestock] = useState(false);

  const fetchReorderSuggestions = async () => {
    setReorderLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/inventory/reorder-suggestions?days=14`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) setReorderData(await r.json());
      else toast.error('Could not load suggestions');
    } catch (e) { toast.error('Could not load suggestions'); }
    finally { setReorderLoading(false); }
  };


  const fetchStock = useCallback(async () => {
    try {
      const [stockRes, logsRes, dashboardRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stock`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/stock/logs`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/stock/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        })
      ]);

      if (stockRes.ok) {
        const items = await stockRes.json();
        setStockItems(items);
        // Extract unique categories
        const cats = [...new Set(items.map(i => i.category).filter(Boolean))];
        setCategories(cats);
        // Initialize reconciliation items
        setReconciliationItems(items.map(item => ({
          item_id: item.item_id,
          name: item.name,
          expected: item.stock_quantity || 0,
          actual: item.stock_quantity || 0,
          reason: ''
        })));
      }
      if (logsRes.ok) setStockLogs(await logsRes.json());
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
    } catch (error) {
      console.error('Error fetching stock:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  // Update reconciliation item
  const updateReconciliationItem = (itemId, field, value) => {
    setReconciliationItems(prev => prev.map(item => 
      item.item_id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Submit reconciliation
  const submitReconciliation = async () => {
    const itemsWithVariance = reconciliationItems.filter(
      item => item.actual !== item.expected
    );
    
    if (itemsWithVariance.length === 0) {
      toast.info('No variances to submit - all counts match expected');
      return;
    }
    
    setSubmittingRecon(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/stock/reconciliation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: format(new Date(), 'yyyy-MM-dd'),
          items: reconciliationItems.map(item => ({
            item_id: item.item_id,
            actual_quantity: item.actual,
            reason: item.reason
          })),
          notes: reconciliationNotes
        })
      });
      
      if (!response.ok) throw new Error('Failed to submit reconciliation');
      
      const result = await response.json();
      toast.success(`Reconciliation submitted. ${itemsWithVariance.length} item(s) adjusted.`);
      fetchStock();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingRecon(false);
    }
  };

  // Open restock dialog
  const openRestockDialog = () => {
    setRestockItems(stockItems.map(item => ({
      item_id: item.item_id,
      name: item.name,
      current: item.stock_quantity || 0,
      quantity: 0,
      cost_per_unit: 0
    })));
    setRestockSupplier('');
    setRestockNote('');
    setRestockDialogOpen(true);
  };

  // Update restock item
  const updateRestockItem = (itemId, field, value) => {
    setRestockItems(prev => prev.map(item => 
      item.item_id === itemId ? { ...item, [field]: parseFloat(value) || 0 } : item
    ));
  };

  // Submit restock
  const submitRestock = async () => {
    const itemsToRestock = restockItems.filter(item => item.quantity > 0);
    
    if (itemsToRestock.length === 0) {
      toast.error('Please add quantities for at least one item');
      return;
    }
    
    setSubmittingRestock(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/stock/restock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: format(new Date(), 'yyyy-MM-dd'),
          items: itemsToRestock.map(item => ({
            item_id: item.item_id,
            quantity: item.quantity,
            cost_per_unit: item.cost_per_unit
          })),
          supplier: restockSupplier,
          delivery_note: restockNote
        })
      });
      
      if (!response.ok) throw new Error('Failed to record restock');
      
      const result = await response.json();
      toast.success(`Restock recorded. ${itemsToRestock.length} item(s) restocked.`);
      setRestockDialogOpen(false);
      fetchStock();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingRestock(false);
    }
  };

  const updateStock = async () => {
    if (!selectedItem || newQuantity === '') return;

    try {
      const response = await fetch(`${API_URL}/api/admin/stock/${selectedItem.item_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          quantity: parseInt(newQuantity),
          reason: reason || 'Manual adjustment'
        })
      });

      if (!response.ok) throw new Error('Failed to update stock');

      toast.success('Stock updated successfully');
      setAdjustDialogOpen(false);
      setSelectedItem(null);
      setNewQuantity('');
      setReason('');
      fetchStock();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openAdjustDialog = (item) => {
    setSelectedItem(item);
    setNewQuantity(item.stock_quantity?.toString() || '0');
    setReason('');
    setAdjustDialogOpen(true);
  };

  const getChangeIcon = (changeType) => {
    const config = changeTypeConfig[changeType] || changeTypeConfig.adjustment;
    const Icon = config.icon;
    return <Icon className={`h-4 w-4 ${config.color}`} />;
  };

  const getChangeBadge = (changeType) => {
    const config = changeTypeConfig[changeType] || changeTypeConfig.adjustment;
    return (
      <Badge className={`${config.bg} ${config.color} border-0`}>
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-ZA', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter items
  const filteredItems = stockItems.filter(item => {
    if (filterCategory !== 'all' && item.category !== filterCategory) return false;
    return true;
  });

  // Filter logs
  const filteredLogs = stockLogs.filter(log => {
    if (filterItem !== 'all' && log.item_id !== filterItem) return false;
    return true;
  });

  const lowStockItems = stockItems.filter(item => (item.stock_quantity || 0) < 10);
  const totalStockValue = stockItems.reduce((acc, item) => acc + (item.stock_quantity || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="stock-loading">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-stock">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900 flex items-center gap-3">
            <Package className="h-8 w-8 text-amber-500" />
            Stock Management
          </h1>
          <p className="text-slate-500 mt-1">Monitor and manage inventory levels with audit trail</p>
        </div>
        <Button onClick={fetchStock} variant="outline">
          <RefreshCcw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* AI Reorder Suggestions */}
      <Card className="mb-6 border-primary/20" data-testid="ai-reorder-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Brain className="h-5 w-5 text-primary" /> AI Reorder Suggestions</CardTitle>
              <CardDescription>Items running low based on 14-day sales velocity</CardDescription>
            </div>
            <Button onClick={fetchReorderSuggestions} disabled={reorderLoading} className="bg-primary" data-testid="get-reorder-btn">
              {reorderLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {reorderData ? 'Refresh' : 'Get suggestions'}
            </Button>
          </div>
        </CardHeader>
        {reorderData && (
          <CardContent>
            {reorderData.ai_summary && (
              <div className="bg-primary/5 border border-primary/10 rounded-lg p-3 mb-4 text-sm text-slate-700 flex gap-2">
                <Brain className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <p>{reorderData.ai_summary}</p>
              </div>
            )}
            {reorderData.suggestions.length === 0 ? (
              <p className="text-slate-500 text-sm py-2">✅ All items have healthy stock cover. Nothing urgent to reorder.</p>
            ) : (
              <div className="space-y-2" data-testid="reorder-list">
                {reorderData.suggestions.map(s => (
                  <div key={s.item_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Badge className={s.urgency === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                      {s.urgency === 'high' ? 'Urgent' : 'Soon'}
                    </Badge>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.current_stock} in stock · {s.daily_velocity}/day · ~{s.days_of_cover} days cover</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">+{s.suggested_reorder}</p>
                      <p className="text-xs text-slate-400">suggested</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-900">{stockItems.length}</p>
                <p className="text-sm text-blue-700">Total Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-900">{totalStockValue}</p>
                <p className="text-sm text-green-700">Total Units</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`${lowStockItems.length > 0 ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`h-8 w-8 ${lowStockItems.length > 0 ? 'text-orange-600' : 'text-slate-400'}`} />
              <div>
                <p className={`text-2xl font-bold ${lowStockItems.length > 0 ? 'text-orange-900' : 'text-slate-900'}`}>
                  {lowStockItems.length}
                </p>
                <p className={`text-sm ${lowStockItems.length > 0 ? 'text-orange-700' : 'text-slate-500'}`}>
                  Low Stock Items
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <History className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-900">{stockLogs.length}</p>
                <p className="text-sm text-purple-700">Recent Changes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <Card className="mb-6 border-orange-300 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-orange-800">Low Stock Alert</p>
                <p className="text-sm text-orange-700 mb-2">
                  The following {lowStockItems.length} item(s) have less than 10 units in stock:
                </p>
                <div className="flex flex-wrap gap-2">
                  {lowStockItems.map(item => (
                    <Badge key={item.item_id} variant="outline" className="bg-white border-orange-300 text-orange-800">
                      {item.name}: {item.stock_quantity || 0} units
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="levels" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="levels">Stock Levels</TabsTrigger>
          <TabsTrigger value="reconciliation">EOD Reconciliation</TabsTrigger>
          <TabsTrigger value="restock">Morning Restock</TabsTrigger>
          <TabsTrigger value="logs">Change History</TabsTrigger>
        </TabsList>

        {/* Stock Levels Tab */}
        <TabsContent value="levels">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Current Stock Levels</CardTitle>
                <CardDescription>Click any item to adjust stock</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button onClick={openRestockDialog} variant="outline" size="sm">
                  <Truck className="h-4 w-4 mr-2" />
                  Record Restock
                </Button>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-40">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredItems.map(item => (
                  <div 
                    key={item.item_id}
                    onClick={() => openAdjustDialog(item)}
                    className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors hover:bg-slate-100 ${
                      (item.stock_quantity || 0) < 10 ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50'
                    }`}
                    data-testid={`stock-item-${item.item_id}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{item.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {item.category && (
                          <Badge variant="outline" className="text-xs">{item.category}</Badge>
                        )}
                        {(item.stock_quantity || 0) < 10 && (
                          <Badge className="bg-orange-100 text-orange-700 border-0 text-xs">Low Stock</Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${
                        (item.stock_quantity || 0) < 10 ? 'text-orange-600' : 'text-slate-900'
                      }`}>
                        {item.stock_quantity || 0}
                      </p>
                      <p className="text-xs text-slate-500">units</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* EOD Reconciliation Tab */}
        <TabsContent value="reconciliation">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5" />
                    End of Day Reconciliation
                  </CardTitle>
                  <CardDescription>
                    Count physical stock and record variances - {format(new Date(), 'EEEE, MMMM d, yyyy')}
                  </CardDescription>
                </div>
                {dashboard?.today_status?.reconciliation_done && (
                  <Badge className="bg-green-100 text-green-700 border-0">
                    <CheckCircle className="h-3 w-3 mr-1" /> Already Submitted
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[400px] overflow-y-auto mb-4">
                {reconciliationItems.map(item => {
                  const variance = item.actual - item.expected;
                  const hasVariance = variance !== 0;
                  return (
                    <div 
                      key={item.item_id}
                      className={`p-4 rounded-lg border ${hasVariance ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-slate-900">{item.name}</p>
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-xs text-slate-500">Expected</p>
                            <p className="font-semibold">{item.expected}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-slate-500">Actual</p>
                            <Input
                              type="number"
                              value={item.actual}
                              onChange={(e) => updateReconciliationItem(item.item_id, 'actual', parseInt(e.target.value) || 0)}
                              className="w-20 text-center font-semibold"
                            />
                          </div>
                          {hasVariance && (
                            <div className="text-center">
                              <p className="text-xs text-slate-500">Variance</p>
                              <p className={`font-bold ${variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {variance > 0 ? '+' : ''}{variance}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      {hasVariance && (
                        <Input
                          placeholder="Reason for variance (e.g., damaged, theft, count error)"
                          value={item.reason}
                          onChange={(e) => updateReconciliationItem(item.item_id, 'reason', e.target.value)}
                          className="mt-2"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="space-y-3 pt-4 border-t">
                <div>
                  <Label>Additional Notes</Label>
                  <Input
                    placeholder="Any additional notes for this reconciliation..."
                    value={reconciliationNotes}
                    onChange={(e) => setReconciliationNotes(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button 
                  onClick={submitReconciliation} 
                  disabled={submittingRecon}
                  className="w-full"
                  data-testid="submit-reconciliation"
                >
                  {submittingRecon ? 'Submitting...' : 'Submit Reconciliation'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Morning Restock Tab */}
        <TabsContent value="restock">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    Morning Restock
                  </CardTitle>
                  <CardDescription>
                    Record incoming stock from suppliers
                  </CardDescription>
                </div>
                <Button onClick={openRestockDialog} data-testid="open-restock-dialog">
                  <Plus className="h-4 w-4 mr-2" />
                  Record New Restock
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {dashboard?.today_status?.restock_done ? (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Today's restock recorded</span>
                    <Badge variant="outline" className="ml-auto">
                      {dashboard.today_status.restock_items_count} items
                    </Badge>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Clock className="h-5 w-5" />
                    <span className="font-medium">No restock recorded today</span>
                  </div>
                </div>
              )}
              
              {/* Low stock items that need restocking */}
              {lowStockItems.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-slate-900 mb-2">Items Needing Restock</h4>
                  <div className="space-y-2">
                    {lowStockItems.map(item => (
                      <div 
                        key={item.item_id}
                        className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{item.name}</p>
                          <p className="text-sm text-orange-600">Current: {item.stock_quantity || 0} units</p>
                        </div>
                        <Badge className="bg-orange-100 text-orange-700 border-0">
                          Low Stock
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Movement Analysis Summary */}
              {dashboard?.movements_7_days && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    7-Day Movement Summary
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-600">{dashboard.movements_7_days.additions}</p>
                      <p className="text-xs text-slate-500">Additions</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{dashboard.movements_7_days.deductions}</p>
                      <p className="text-xs text-slate-500">Deductions</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{dashboard.movements_7_days.total_movements}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Change History Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Stock Change History</CardTitle>
                <CardDescription>Audit trail of all stock adjustments</CardDescription>
              </div>
              <Select value={filterItem} onValueChange={setFilterItem}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by item" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  {stockItems.map(item => (
                    <SelectItem key={item.item_id} value={item.item_id}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {filteredLogs.length > 0 ? (
                  filteredLogs.map(log => (
                    <div 
                      key={log.log_id} 
                      className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                      data-testid={`log-${log.log_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getChangeIcon(log.change_type)}
                          <p className="font-medium text-slate-900">{log.item_name}</p>
                          {getChangeBadge(log.change_type)}
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold">
                            <span className="text-slate-500">{log.previous_quantity}</span>
                            <span className="mx-2">→</span>
                            <span className={log.new_quantity > log.previous_quantity ? 'text-green-600' : 'text-red-600'}>
                              {log.new_quantity}
                            </span>
                          </span>
                          <p className="text-xs text-slate-500">
                            {log.new_quantity > log.previous_quantity ? '+' : ''}{log.new_quantity - log.previous_quantity} units
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <p className="text-slate-600">
                          <strong>Reason:</strong> {log.reason}
                        </p>
                        <p className="text-slate-500 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(log.created_at)}
                        </p>
                      </div>
                      {log.order_id && (
                        <p className="text-xs text-purple-600 mt-1">
                          <ShoppingCart className="h-3 w-3 inline mr-1" />
                          Order: #{log.order_id.slice(-8).toUpperCase()}
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-slate-500 py-8">No stock changes recorded</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Adjust Stock Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Adjust Stock - {selectedItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="text-center p-4 bg-slate-100 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">Current Stock</p>
              <p className="text-4xl font-bold text-slate-900">{selectedItem?.stock_quantity || 0}</p>
              <p className="text-sm text-slate-500">units</p>
            </div>
            <div>
              <Label htmlFor="newQty">New Quantity</Label>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setNewQuantity(prev => Math.max(0, parseInt(prev || 0) - 1).toString())}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="newQty"
                  type="number"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(e.target.value)}
                  className="text-center text-xl font-bold"
                  data-testid="new-quantity"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setNewQuantity(prev => (parseInt(prev || 0) + 1).toString())}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="reason">Reason for Adjustment</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-2" data-testid="adjustment-reason">
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New delivery">New delivery</SelectItem>
                  <SelectItem value="Inventory count correction">Inventory count correction</SelectItem>
                  <SelectItem value="Damaged goods">Damaged goods</SelectItem>
                  <SelectItem value="Expired items">Expired items</SelectItem>
                  <SelectItem value="Kitchen prep">Kitchen prep</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reason === 'Other' && (
              <div>
                <Label htmlFor="customReason">Custom Reason</Label>
                <Input
                  id="customReason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason..."
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={updateStock}
              className="bg-primary hover:bg-primary-600"
              disabled={!reason}
              data-testid="confirm-adjustment"
            >
              Update Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restock Dialog */}
      <Dialog open={restockDialogOpen} onOpenChange={setRestockDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Record Morning Restock
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="supplier">Supplier Name</Label>
                <Input
                  id="supplier"
                  value={restockSupplier}
                  onChange={(e) => setRestockSupplier(e.target.value)}
                  placeholder="e.g., Metro Cash & Carry"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="deliveryNote">Delivery Note #</Label>
                <Input
                  id="deliveryNote"
                  value={restockNote}
                  onChange={(e) => setRestockNote(e.target.value)}
                  placeholder="e.g., DN12345"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Item</th>
                    <th className="text-center p-2 w-20">Current</th>
                    <th className="text-center p-2 w-24">Add Qty</th>
                    <th className="text-center p-2 w-24">Cost/Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {restockItems.map(item => (
                    <tr key={item.item_id} className="border-t">
                      <td className="p-2">
                        <p className="font-medium">{item.name}</p>
                        {item.current < 10 && (
                          <Badge className="bg-orange-100 text-orange-700 border-0 text-xs">Low</Badge>
                        )}
                      </td>
                      <td className="text-center p-2">{item.current}</td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="0"
                          value={item.quantity || ''}
                          onChange={(e) => updateRestockItem(item.item_id, 'quantity', e.target.value)}
                          className="text-center h-8"
                          placeholder="0"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.cost_per_unit || ''}
                          onChange={(e) => updateRestockItem(item.item_id, 'cost_per_unit', e.target.value)}
                          className="text-center h-8"
                          placeholder="R0"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Summary */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Items to restock:</span>
                <span className="font-bold">{restockItems.filter(i => i.quantity > 0).length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Total units:</span>
                <span className="font-bold">{restockItems.reduce((sum, i) => sum + (i.quantity || 0), 0)}</span>
              </div>
              <div className="flex justify-between text-sm border-t mt-2 pt-2">
                <span>Estimated cost:</span>
                <span className="font-bold">
                  R{restockItems.reduce((sum, i) => sum + ((i.quantity || 0) * (i.cost_per_unit || 0)), 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRestockDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitRestock}
              disabled={submittingRestock || restockItems.filter(i => i.quantity > 0).length === 0}
              data-testid="confirm-restock"
            >
              {submittingRestock ? 'Recording...' : 'Record Restock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
